import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { TagGroup, TagList } from "react-aria-components";
import Tag from "../../../core/basic/Tag";
import { CustomTriggerTooltip } from "../../../core/basic/Tooltip";
import { css } from "../../../../../styled-system/css";
import { token } from "../../../../../styled-system/tokens";
import { useAppSelector } from "../../../../redux/hooks";
import { formatCurrency } from "../../../../utils/formatNumber";
import { useDateFormatter } from "../../../../i18n/useDateFormatter";
import moment from "moment";

type ScheduledActionField = {
  field_name?: string;
  change_value?: number;
};

type ScheduledAction = {
  scheduled_action_status?: string;
  scheduled_action_start_date?: string;
  scheduled_action_end_date?: string;
  scheduled_action_fields?: ScheduledActionField[];
};

type TargetBadgesProps = {
  target: {
    manual_bid?: number | null;
    ceil_bid?: number | null;
    floor_bid?: number | null;
    scheduled_actions?: ScheduledAction[] | null;
    tags?: string[] | null;
  };
};

const tagListStyles = css({
  display: "flex",
  flexWrap: "wrap",
  alignItems: "center",
  gap: "xsmall",
});

export const TargetBadges = ({ target }: TargetBadgesProps) => {
  const { t } = useTranslation(["advertising"]);
  const { marketPlace } = useAppSelector((state) => state.marketPlace);
  const fmt = useDateFormatter("monthDay");
  const { manual_bid, ceil_bid, floor_bid, scheduled_actions: scheduledActions, tags } = target;

  const filteredScheduledActions = useMemo(() => {
    return (
      scheduledActions?.filter(
        (sa) =>
          sa.scheduled_action_status !== undefined && ["scheduled", "active"].includes(sa.scheduled_action_status),
      ) ?? []
    );
  }, [scheduledActions]);

  const formatScheduledActionLabel = (scheduledAction: ScheduledAction): string => {
    const field = scheduledAction.scheduled_action_fields?.[0];
    const startDate = fmt.format(moment(scheduledAction.scheduled_action_start_date).utc().toDate());
    const endDate = fmt.format(moment(scheduledAction.scheduled_action_end_date).utc().toDate());

    if (!field) return "";

    const { field_name, change_value } = field;

    if (field_name === "timebound_per_change" && change_value != null) {
      const percentChange = Math.round(change_value * 100) - 100;
      const sign = percentChange > 0 ? "+" : "";
      const percent = `${sign}${Math.abs(percentChange)}`;
      return percentChange > 0 ?
          t(($) => $.advertising["{percent}% Bid Boost: {start}-{end}"], { percent, start: startDate, end: endDate })
        : t(($) => $.advertising["{percent}% Bid Reduction: {start}-{end}"], {
            percent,
            start: startDate,
            end: endDate,
          });
    }

    if (field_name === "manual_bid" && change_value != null) {
      const bidValue = formatCurrency(change_value, { marketPlace });
      return t(($) => $.advertising["{bid} Manual Bid: {start}-{end}"], {
        bid: bidValue,
        start: startDate,
        end: endDate,
      });
    }

    if (field_name === "timebound_flat_change" && change_value != null) {
      const boostValue = formatCurrency(Math.abs(change_value), { marketPlace });
      const sign = change_value > 0 ? "+" : "";
      const bid = `${sign}${boostValue}`;
      return change_value > 0 ?
          t(($) => $.advertising["{bid} Bid Boost: {start}-{end}"], { bid, start: startDate, end: endDate })
        : t(($) => $.advertising["{bid} Bid Reduction: {start}-{end}"], { bid, start: startDate, end: endDate });
    }

    return "";
  };

  const items: Array<{ id: string; label: string; color?: string; tooltip?: string }> = [];

  if (manual_bid) {
    items.push({
      id: "manual-bid",
      label: t(($) => $.advertising["Manual Bid"]),
      tooltip: t(($) => $.advertising["Manual bid set to {bid}"], { bid: formatCurrency(manual_bid, { marketPlace }) }),
    });
  }
  if (ceil_bid || floor_bid) {
    items.push({
      id: "bid-range",
      label: t(($) => $.advertising["Bid Range: {floor}-{ceil}"], {
        floor: formatCurrency(floor_bid, { marketPlace }),
        ceil: formatCurrency(ceil_bid, { marketPlace }),
      }),
      color: token("colors.fuchsia"),
    });
  }
  for (let i = 0; i < filteredScheduledActions.length; i++) {
    const sa = filteredScheduledActions[i];
    if (!sa) continue;
    const label = formatScheduledActionLabel(sa);
    if (label) {
      items.push({ id: `scheduled-${i}`, label, color: token("colors.fuchsia") });
    }
  }
  if (tags?.length) {
    for (const tag of tags) {
      items.push({ id: `tag-${tag}`, label: tag });
    }
  }

  if (items.length === 0) return null;

  return (
    <TagGroup aria-label={t(($) => $.advertising["Target badges"])}>
      <TagList className={tagListStyles} items={items}>
        {(item) => (
          <CustomTriggerTooltip toolTip={item.tooltip}>
            <Tag tag={item} color={item.color} />
          </CustomTriggerTooltip>
        )}
      </TagList>
    </TagGroup>
  );
};
